import { createContext, useContext, useEffect, useState, type AnchorHTMLAttributes, type MouseEvent } from "react";

/** Tiny history router - the site has a handful of routes, a router library would be overkill. */
type Loc = { pathname: string; search: URLSearchParams };
const RouterCtx = createContext<{ loc: Loc; navigate: (to: string) => void } | null>(null);

function read(): Loc { return { pathname: window.location.pathname, search: new URLSearchParams(window.location.search) }; }

export function RouterProvider({ children }: { children: React.ReactNode }) {
  const [loc, setLoc] = useState(read);
  useEffect(() => {
    const on = () => setLoc(read());
    window.addEventListener("popstate", on);
    return () => window.removeEventListener("popstate", on);
  }, []);
  const navigate = (to: string) => {
    window.history.pushState(null, "", to);
    setLoc(read());
    window.scrollTo({ top: 0 });
  };
  return <RouterCtx.Provider value={{ loc, navigate }}>{children}</RouterCtx.Provider>;
}

export function useRouter() {
  const ctx = useContext(RouterCtx);
  if (!ctx) throw new Error("useRouter outside RouterProvider");
  return ctx;
}

export function Link({ href, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  const { navigate } = useRouter();
  return (
    <a
      href={href}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(e);
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0 || /^https?:/.test(href)) return;
        e.preventDefault();
        navigate(href);
      }}
      {...rest}
    />
  );
}
